/**
 * Trust & Safety Dashboard - Fraud Detection and Prevention
 * Interactive map, alerts, trust scores, and fraud analytics
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  MapPin,
  Eye,
  RefreshCw,
  Search,
  Filter,
  Clock,
  TrendingDown,
  TrendingUp,
  Activity,
  UserX,
  FileCheck,
  Ban,
  Unlock
} from 'lucide-react';

// Jamaican Color Palette
const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
};

// Severity colors
const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500', dot: '#ef4444' },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500', dot: '#f97316' },
  medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500', dot: '#eab308' },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500', dot: '#3b82f6' }
};

// Trust status colors
const TRUST_STATUS_COLORS = {
  excellent: 'text-green-400',
  good: 'text-emerald-400',
  caution: 'text-yellow-400',
  warning: 'text-orange-400',
  blocked: 'text-red-400',
  suspended: 'text-red-600'
};

// ============== MAP COMPONENT ==============

function FraudHeatmap({ points }) {
  // Jamaica center coordinates
  const jamaicaCenter = [18.1096, -77.2975];
  
  const getMarkerColor = (severity) => {
    return SEVERITY_COLORS[severity]?.dot || '#3b82f6';
  };
  
  const getMarkerSize = (count) => {
    return Math.min(8 + count * 2, 25);
  };

  return (
    <MapContainer 
      center={jamaicaCenter} 
      zoom={9} 
      style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}
      className="z-0"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {points.map((point, i) => (
        <CircleMarker
          key={i}
          center={[point.lat, point.lng]}
          radius={getMarkerSize(point.count)}
          fillColor={getMarkerColor(point.severity)}
          color={getMarkerColor(point.severity)}
          weight={2}
          opacity={0.8}
          fillOpacity={0.5}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold">{point.flag_type.replace(/_/g, ' ')}</p>
              <p>Severity: <span className="capitalize">{point.severity}</span></p>
              <p>Incidents: {point.count}</p>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

// ============== SUMMARY CARDS ==============

function SummaryCard({ title, value, icon: Icon, color, trend, subtitle }) {
  return (
    <Card className="p-4 bg-slate-800/50 border-slate-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color.replace('text-', 'bg-')}/20 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-red-400' : 'text-green-400'}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{Math.abs(trend)}% from yesterday</span>
        </div>
      )}
    </Card>
  );
}

// ============== ALERTS TABLE ==============

function AlertsTable({ alerts, onResolve, onViewDetails }) {
  const getSeverityBadge = (severity) => {
    const config = SEVERITY_COLORS[severity] || SEVERITY_COLORS.low;
    return (
      <Badge className={`${config.bg} ${config.text} ${config.border} border capitalize`}>
        {severity}
      </Badge>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Severity</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Type</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">User</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Time</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map(alert => (
            <tr key={alert.id} className="border-b border-slate-800 hover:bg-slate-800/50">
              <td className="py-3 px-4">{getSeverityBadge(alert.severity)}</td>
              <td className="py-3 px-4">
                <span className="text-white">{alert.flag_type.replace(/_/g, ' ')}</span>
              </td>
              <td className="py-3 px-4">
                <p className="text-white text-sm">{alert.user_name}</p>
                <p className="text-xs text-slate-500">{alert.user_id.substring(0, 8)}...</p>
              </td>
              <td className="py-3 px-4 text-sm text-slate-400">
                {new Date(alert.created_at).toLocaleString()}
              </td>
              <td className="py-3 px-4">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewDetails(alert)}
                    className="border-slate-600"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => onResolve(alert.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Resolve
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {alerts.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No active alerts</p>
        </div>
      )}
    </div>
  );
}

// ============== TRUST SCORES TABLE ==============

function TrustScoresTable({ scores, onSuspend, onUnsuspend, onRecalculate }) {
  const getTrustScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getStatusBadge = (status) => {
    const colors = TRUST_STATUS_COLORS[status] || 'text-slate-400';
    return (
      <Badge className={`${colors.replace('text-', 'bg-')}/20 ${colors} capitalize`}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">User</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Role</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Score</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Status</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Flags</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Verified</th>
            <th className="text-left py-3 px-4 text-sm text-slate-400 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {scores.map(score => (
            <tr key={`${score.user_id}-${score.role}`} className="border-b border-slate-800 hover:bg-slate-800/50">
              <td className="py-3 px-4">
                <p className="text-white text-sm">{score.user_name}</p>
                <p className="text-xs text-slate-500">{score.user_email}</p>
              </td>
              <td className="py-3 px-4">
                <Badge className="bg-slate-700 text-slate-300 capitalize">{score.role}</Badge>
              </td>
              <td className="py-3 px-4">
                <span className={`text-2xl font-bold ${getTrustScoreColor(score.trust_score)}`}>
                  {score.trust_score}
                </span>
              </td>
              <td className="py-3 px-4">{getStatusBadge(score.status)}</td>
              <td className="py-3 px-4">
                <span className={`${score.flags_count > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {score.flags_count}
                </span>
              </td>
              <td className="py-3 px-4">
                {score.verified ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-slate-500" />
                )}
              </td>
              <td className="py-3 px-4">
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onRecalculate(score.user_id, score.role)}
                    className="border-slate-600"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                  {score.status === 'suspended' ? (
                    <Button 
                      size="sm"
                      onClick={() => onUnsuspend(score.user_id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Unlock className="w-3 h-3 mr-1" />
                      Unsuspend
                    </Button>
                  ) : (
                    <Button 
                      size="sm"
                      variant="destructive"
                      onClick={() => onSuspend(score.user_id)}
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Suspend
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============== VERIFICATION QUEUE ==============

function VerificationQueue({ verifications, onApprove, onReject }) {
  return (
    <div className="space-y-3">
      {verifications.map(v => (
        <Card key={v.id} className="p-4 bg-slate-800/50 border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <p className="font-medium text-white">{v.user_name}</p>
                <p className="text-sm text-slate-400">{v.user_email}</p>
                <div className="flex gap-2 mt-1">
                  <Badge className="bg-slate-700 text-slate-300 capitalize">{v.role}</Badge>
                  <Badge className="bg-slate-700 text-slate-300">{v.id_type.replace(/_/g, ' ')}</Badge>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right text-sm text-slate-400">
                <p>Submitted</p>
                <p>{new Date(v.created_at).toLocaleDateString()}</p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  variant="destructive"
                  onClick={() => onReject(v.id)}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => onApprove(v.id)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
      
      {verifications.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <FileCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No pending verifications</p>
        </div>
      )}
    </div>
  );
}

// ============== ALERT DETAILS MODAL ==============

function AlertDetailsModal({ alert, onClose }) {
  if (!alert) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Alert Details</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <XCircle className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-400">Type</p>
              <p className="text-white">{alert.flag_type.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Severity</p>
              <Badge className={`${SEVERITY_COLORS[alert.severity]?.bg} ${SEVERITY_COLORS[alert.severity]?.text} capitalize`}>
                {alert.severity}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-400">User</p>
              <p className="text-white">{alert.user_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Time</p>
              <p className="text-white">{new Date(alert.created_at).toLocaleString()}</p>
            </div>
          </div>
          
          {alert.package_id && (
            <div>
              <p className="text-sm text-slate-400">Package ID</p>
              <p className="text-white font-mono">{alert.package_id}</p>
            </div>
          )}
          
          <div>
            <p className="text-sm text-slate-400 mb-2">Details</p>
            <pre className="bg-slate-900 p-3 rounded-lg text-sm text-slate-300 overflow-auto max-h-48">
              {JSON.stringify(alert.details, null, 2)}
            </pre>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  );
}

// ============== MAIN COMPONENT ==============

export default function TrustDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [heatmapPoints, setHeatmapPoints] = useState([]);
  const [trustScores, setTrustScores] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, alertsRes, heatmapRes, scoresRes, verificationsRes] = await Promise.all([
        api.get('/security/dashboard/summary'),
        api.get('/security/dashboard/alerts?limit=50'),
        api.get('/security/dashboard/heatmap'),
        api.get('/security/trust-scores?limit=100'),
        api.get('/security/verify/pending?limit=50')
      ]);
      
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setHeatmapPoints(heatmapRes.data.points || []);
      setTrustScores(scoresRes.data.scores || []);
      setVerifications(verificationsRes.data.verifications || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAlert = async (flagId) => {
    const notes = prompt('Enter resolution notes:');
    if (!notes) return;
    
    try {
      await api.post('/security/fraud-flags/resolve', {
        flag_id: flagId,
        resolution_notes: notes
      });
      toast.success('Alert resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleApproveVerification = async (verificationId) => {
    try {
      await api.post('/security/verify/review', {
        verification_id: verificationId,
        approved: true
      });
      toast.success('Verification approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve verification');
    }
  };

  const handleRejectVerification = async (verificationId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      await api.post('/security/verify/review', {
        verification_id: verificationId,
        approved: false,
        rejection_reason: reason
      });
      toast.success('Verification rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject verification');
    }
  };

  const handleSuspendUser = async (userId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;
    
    try {
      await api.post(`/security/suspend-user?user_id=${userId}&reason=${encodeURIComponent(reason)}`);
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleUnsuspendUser = async (userId) => {
    try {
      await api.post(`/security/unsuspend-user?user_id=${userId}`);
      toast.success('User unsuspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to unsuspend user');
    }
  };

  const handleRecalculateScore = async (userId, role) => {
    try {
      await api.post(`/security/trust-scores/recalculate?user_id=${userId}&role=${role}`);
      toast.success('Trust score recalculated');
      fetchData();
    } catch (error) {
      toast.error('Failed to recalculate score');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading trust dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/ceo')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  Trust & Safety Dashboard
                </h1>
                <p className="text-sm text-slate-400">
                  Fraud detection and platform security
                </p>
              </div>
            </div>
            
            <Button onClick={fetchData} variant="outline" className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-4 overflow-x-auto">
          {['overview', 'alerts', 'trust-scores', 'verifications', 'heatmap'].map(tab => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-green-600' : ''}
            >
              {tab === 'overview' && <Activity className="w-4 h-4 mr-2" />}
              {tab === 'alerts' && <AlertTriangle className="w-4 h-4 mr-2" />}
              {tab === 'trust-scores' && <Users className="w-4 h-4 mr-2" />}
              {tab === 'verifications' && <FileCheck className="w-4 h-4 mr-2" />}
              {tab === 'heatmap' && <MapPin className="w-4 h-4 mr-2" />}
              {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              {tab === 'alerts' && alerts.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{alerts.length}</Badge>
              )}
              {tab === 'verifications' && verifications.length > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-black">{verifications.length}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard 
                title="Flags (24h)" 
                value={summary?.total_flags_24h || 0}
                icon={AlertTriangle}
                color="text-yellow-400"
              />
              <SummaryCard 
                title="Critical/High" 
                value={`${summary?.critical_flags || 0}/${summary?.high_flags || 0}`}
                icon={AlertCircle}
                color="text-red-400"
              />
              <SummaryCard 
                title="Pending Verifications" 
                value={summary?.pending_verifications || 0}
                icon={FileCheck}
                color="text-blue-400"
              />
              <SummaryCard 
                title="Blocked Users" 
                value={summary?.blocked_users || 0}
                icon={UserX}
                color="text-orange-400"
              />
            </div>
            
            {/* Trust Score Overview */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-slate-800/50 border-slate-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-400" />
                  Average Trust Scores
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-green-400">
                      {summary?.avg_driver_trust_score?.toFixed(0) || 100}
                    </p>
                    <p className="text-sm text-slate-400">Drivers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-blue-400">
                      {summary?.avg_merchant_trust_score?.toFixed(0) || 100}
                    </p>
                    <p className="text-sm text-slate-400">Merchants</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-slate-800/50 border-slate-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Flag Distribution (24h)
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center p-3 bg-red-500/10 rounded-lg">
                    <p className="text-xl font-bold text-red-400">{summary?.critical_flags || 0}</p>
                    <p className="text-xs text-slate-400">Critical</p>
                  </div>
                  <div className="text-center p-3 bg-orange-500/10 rounded-lg">
                    <p className="text-xl font-bold text-orange-400">{summary?.high_flags || 0}</p>
                    <p className="text-xs text-slate-400">High</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                    <p className="text-xl font-bold text-yellow-400">{summary?.medium_flags || 0}</p>
                    <p className="text-xs text-slate-400">Medium</p>
                  </div>
                  <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                    <p className="text-xl font-bold text-blue-400">{summary?.low_flags || 0}</p>
                    <p className="text-xs text-slate-400">Low</p>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Recent Critical Alerts */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Recent Critical/High Alerts
              </h3>
              <AlertsTable 
                alerts={alerts.filter(a => ['critical', 'high'].includes(a.severity)).slice(0, 5)}
                onResolve={handleResolveAlert}
                onViewDetails={setSelectedAlert}
              />
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">All Active Alerts</h3>
              <span className="text-slate-400">{alerts.length} alerts</span>
            </div>
            <AlertsTable 
              alerts={alerts}
              onResolve={handleResolveAlert}
              onViewDetails={setSelectedAlert}
            />
          </Card>
        )}

        {/* Trust Scores Tab */}
        {activeTab === 'trust-scores' && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">User Trust Scores</h3>
              <div className="flex gap-2">
                <select 
                  value={roleFilter}
                  onChange={e => setRoleFilter(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1 text-sm"
                >
                  <option value="">All Roles</option>
                  <option value="driver">Drivers</option>
                  <option value="merchant">Merchants</option>
                  <option value="customer">Customers</option>
                </select>
              </div>
            </div>
            <TrustScoresTable 
              scores={roleFilter ? trustScores.filter(s => s.role === roleFilter) : trustScores}
              onSuspend={handleSuspendUser}
              onUnsuspend={handleUnsuspendUser}
              onRecalculate={handleRecalculateScore}
            />
          </Card>
        )}

        {/* Verifications Tab */}
        {activeTab === 'verifications' && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Pending Verifications</h3>
              <span className="text-slate-400">{verifications.length} pending</span>
            </div>
            <VerificationQueue 
              verifications={verifications}
              onApprove={handleApproveVerification}
              onReject={handleRejectVerification}
            />
          </Card>
        )}

        {/* Heatmap Tab */}
        {activeTab === 'heatmap' && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-400" />
                Fraud Heatmap - Jamaica
              </h3>
              <div className="flex gap-4 text-sm">
                {Object.entries(SEVERITY_COLORS).map(([severity, config]) => (
                  <div key={severity} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: config.dot }} />
                    <span className="text-slate-400 capitalize">{severity}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-[500px] rounded-xl overflow-hidden">
              <FraudHeatmap points={heatmapPoints} />
            </div>
            
            {heatmapPoints.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50">
                <p className="text-slate-400">No location-based fraud data available</p>
              </div>
            )}
          </Card>
        )}
      </main>
      
      {/* Alert Details Modal */}
      <AlertDetailsModal 
        alert={selectedAlert}
        onClose={() => setSelectedAlert(null)}
      />
    </div>
  );
}
